<template>
    <div class="text-center text-primary py-7" v-if="dataLoading">
        <loading-animation class="fs-10"/>
        <p>Please wait while data is being loaded...</p>
    </div>
    <div v-else class="py-5">
        <section-heading size="xlg" title="FLYERS"/>
        <div v-if="flyers && flyers.length" class="about mt-lg-6">
            <div class="flyer-grid">
                <div v-for="flyer in displayedFlyers" :key="flyer.id" class="flyer-card">
                    <h5 class="text-center">{{ flyer.title }}</h5>

                    <div
                          class="flyer-image-wrapper"
                          :style="{
                            ...getFlyerStyle(flyer.aspect_ratio),
                            cursor: flyer.image_2 ? 'pointer' : 'default'
                          }"
                          @click="flyer.image_2 && toggleFlyerImage(flyer.id)"
                        >
                      <img
                        :src="flyerImageToggle[flyer.id] && flyer.image_2 ? flyer.image_2 : flyer.image"
                        :alt="flyer.title"
                        class="flyer-image"
                      />
                    </div>
                    <div v-if="flyer.image_2" class="flyer-bullets-container">
                        <div
                          class="flyer-bullet"
                          :class="{ active: !flyerImageToggle[flyer.id] }"
                          @click="flyerImageToggle[flyer.id] = false"
                        ></div>
                        <div
                          class="flyer-bullet"
                          :class="{ active: flyerImageToggle[flyer.id] }"
                          @click="flyerImageToggle[flyer.id] = true"
                        ></div>
                    </div>
                </div>
            </div>
          <div v-if="!showAll && flyers.length > 3" class="text-center mt-4">
            <btn size="lg" class="px-5 fs-lg-1 font-poppins-regular" @click="showAll = true">
              View All
            </btn>
          </div>
        </div>
    </div>
</template>

<script>

import axios from 'secure-axios';
import urls from '../data/urls';

export default {
    name: 'Flyer',
    data () {
        return {
            dataLoading: false,
            flyers: [],
            showAll: false,
            flyerImageToggle: {}
        };
    },
    computed: {
        displayedFlyers () {
            return this.showAll ? this.flyers : this.flyers.slice(0, 3);
        }
    },
    mounted () {
        this.loadData();
    },
    methods: {
        async loadData () {
            this.dataLoading = true;
            const response = await axios.get(urls.website.flyerPage);
            const json = response.data;
            if (json.error === false) {
                this.flyers = json.data;
                // Use $set to ensure reactivity
                this.flyers.forEach(flyer => {
                    this.$set(this.flyerImageToggle, flyer.id, false);
                });
            } else {
                this.$notify('Could not load data, Please try again later', 'Error', {
                    type: 'danger'
                });
            }
            this.dataLoading = false;
        },
        toggleFlyerImage (flyerId) {
            this.flyerImageToggle[flyerId] = !this.flyerImageToggle[flyerId];
        },
        getFlyerStyle (aspectRatio) {
            if (aspectRatio === '3:4') {
                return {
                    width: '100%',
                    aspectRatio: '3/4',
                    maxWidth: '100%'
                };
            } else if (aspectRatio === '4:3') {
                return {
                    width: '100%',
                    aspectRatio: '4/3',
                    maxWidth: '100%'
                };
            } else {
                // fallback
                return {
                    width: '100%',
                    aspectRatio: '4/3',
                    maxWidth: '100%'
                };
            }
        }
    }
};
</script>

<style scoped>
.flyer-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  padding: 0 1rem;
  margin-bottom: 2rem;
}

.flyer-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem;
  border-radius: 8px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.flyer-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.flyer-image-wrapper {
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.flyer-image {
  width: 100%;
  height: 100%;
  object-fit: cover; /* fill container, cropping if needed */
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.flyer-bullet-below {
  margin-top: 12px;
  font-size: 1.2rem;
  color: black;
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 2px solid white;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.flyer-bullets-container {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.flyer-bullet {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(200, 200, 200, 0.6);
  border: 2px solid rgba(100, 100, 100, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.flyer-bullet:hover {
  background-color: rgba(150, 150, 150, 0.8);
}

.flyer-bullet.active {
  background-color: #000;
  border-color: #000;
  width: 14px;
  height: 14px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.4);
}

.flyer-overlay {
    position: absolute;
    bottom: 10px;
    left: 0;
    width: 100%;
    text-align: center;
    color: white;
    font-size: 1.5rem;
    pointer-events: none;
    z-index: 2;
}

/* Responsive grid for smaller screens */
@media (max-width: 1024px) {
  .flyer-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
}

@media (max-width: 768px) {
  .flyer-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .flyer-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}</style>
